import {
  useGetInvoicesSuspense,
  useGetRolesSuspense,
  useGetUsersSuspense,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makePath } from 'utils/routes';
import { Link } from 'wouter';

import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { BillingToggle } from './BillingToggle';
import {
  ALL_ROLES_VALUE,
  FILLER_BORDER,
  getRoleColor,
  TILE_BORDER,
} from './constants';
import { CreateUserModal } from './CreateUserModal';

type UserRow = {
  id: string;
  externalId: string;
  role: string;
  invoiceCount: number;
};

const userSchema: ColumnDefinition<UserRow>[] = [
  {
    title: 'ID',
    colspan: 'col-span-4',
    format: (row) => (
      <div className="flex items-center gap-x-f1">
        <div
          className={clsx(
            'w-[14px] h-[14px] rounded-full shrink-0',
            getRoleColor(row.role)
          )}
        />
        <span>{row.externalId}</span>
      </div>
    ),
  },
  {
    title: 'Invoices',
    justify: 'right',
    colspan: 'col-span-2',
    format: (row) => (
      <span>{row.invoiceCount > 0 ? String(row.invoiceCount) : '—'}</span>
    ),
  },
];

const ColumnHeaders = () => (
  <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 py-f1 text-xmd text-main-500 border-b-[0.5px] border-main-200">
    <div className="col-span-4">ID</div>
    <div className="col-span-2 text-right">Invoices</div>
  </div>
);

const ViewToggle = ({
  viewMode,
  setViewMode,
  readOnly,
}: {
  viewMode?: 'grid' | 'list';
  setViewMode?: (m: 'grid' | 'list') => void;
  readOnly?: boolean;
}) => (
  <BillingToggle
    readOnly={readOnly}
    initialActiveButton={viewMode === 'list' ? 'right' : 'left'}
    onLeftClick={() => setViewMode?.('grid')}
    onRightClick={() => setViewMode?.('list')}
    leftLabel={<Icon type="gridView" size="sm" />}
    rightLabel={<Icon type="listView" size="sm" />}
    buttonPadding="py-f0"
    buttonHeight="h-fit"
  />
);

const UserGridTile = ({ user, href }: { user: UserRow; href: string }) => (
  <Link href={href}>
    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
    <a
      className={clsx(
        'flex flex-col items-start justify-start w-full p-f1 gap-y-f1 aspect-[8/9] overflow-hidden hover:bg-negative',
        TILE_BORDER
      )}
    >
      <div
        className={clsx(
          'w-f2 h-f2 rounded-full shrink-0',
          getRoleColor(user.role)
        )}
      />
      <span className="text-main text-xmd truncate w-full">
        {user.externalId}
      </span>
    </a>
  </Link>
);

const EmptyUserGridTile = () => (
  <div
    className={clsx(
      'flex flex-col items-start justify-start w-full p-f1 gap-y-f1 aspect-[8/9] overflow-hidden',
      TILE_BORDER
    )}
  >
    <div className="w-f2 h-f2 rounded-full shrink-0 bg-main-200" />
    <span className="text-main-500 text-xmd">No Users Yet</span>
  </div>
);

const userEmptyValue = {
  ID: 'No Users Yet',
  Invoices: '—',
};

const SKELETON_MAX_TILES = 12;

type Props = {
  workspaceId: string;
};

const UsersListPageLoading = ({ workspaceId: _workspaceId }: Props) => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Users"
      secondaryAction={{ title: 'Edit', onClick: () => {}, disabled: true }}
      primaryAction={{ title: 'New', onClick: () => {}, disabled: true }}
    >
      <div className="flex flex-col w-full min-h-0">
        <div className="sticky top-0 z-10 shrink-0">
          <div className="flex items-end w-full">
            <div className="flex-1 border-b-[0.5px] border-main-200 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
            <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-200 px-f1 self-stretch">
              <ViewToggle readOnly />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] w-full min-h-[360px]">
          <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
          {/* eslint-disable react/no-array-index-key */}
          {Array.from({ length: visibleTiles }).map((__, i) => (
            <div
              key={`skeleton-${i}`}
              className={clsx('aspect-[8/9]', TILE_BORDER)}
            />
          ))}
          {Array.from({ length: 8 }).map((__, i) => (
            <div key={`filler-${i}`} className={FILLER_BORDER} />
          ))}
          {/* eslint-enable react/no-array-index-key */}
        </div>
      </div>
    </PaymentViewLayout>
  );
};

const UsersListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Users');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(ALL_ROLES_VALUE);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const { data: usersData } = useGetUsersSuspense();
  const { data: invoicesData } = useGetInvoicesSuspense();
  const { data: rolesData } = useGetRolesSuspense();

  const roleOptions = useMemo(
    () => [
      { value: ALL_ROLES_VALUE, label: 'All' },
      ...rolesData.data
        .map((r) => ({ value: r.role, label: capitalize(r.role) }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    ],
    [rolesData.data]
  );

  const invoiceCountByUser = useMemo(() => {
    const counts: Record<string, number> = {};
    invoicesData.data.forEach((invoice) => {
      const userIds = new Set(
        (invoice.line_items ?? []).map((item) => item.user_id).filter(Boolean)
      );
      userIds.forEach((userId) => {
        counts[userId] = (counts[userId] ?? 0) + 1;
      });
    });
    return counts;
  }, [invoicesData.data]);

  const users: UserRow[] = useMemo(
    () =>
      usersData.data
        .filter(
          (u) => selectedRole === ALL_ROLES_VALUE || u.role === selectedRole
        )
        .map((u) => ({
          id: u.id,
          externalId: u.external_id,
          role: u.role,
          invoiceCount: invoiceCountByUser[u.external_id] ?? 0,
        })),
    [usersData.data, invoiceCountByUser, selectedRole]
  );

  const getPath = useCallback(
    (user: UserRow) =>
      makePath(['w', workspaceId, 'payments', 'users', user.externalId], {
        absolute: true,
      }),
    [workspaceId]
  );

  const handleCreate = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Users"
        secondaryAction={{
          title: 'Edit',
          onClick: () => {},
          disabled: selectedRole === ALL_ROLES_VALUE,
        }}
        primaryAction={{
          title: 'New',
          onClick: handleCreate,
          'data-testid': 'create-user-button',
        }}
      >
        <div className="flex flex-col w-full h-full min-h-0">
          <div className="sticky top-0 z-10 shrink-0">
            <div className="flex items-end w-full">
              <TabsV2
                options={roleOptions}
                selectedValue={selectedRole}
                onClick={setSelectedRole}
              />
              <div className="flex-1 border-b-[0.5px] border-main-200" />
              <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-200 px-f1 self-stretch">
                <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
              </div>
            </div>
            {viewMode === 'list' && <ColumnHeaders />}
          </div>
          {viewMode === 'list' ? (
            <NavTableV2
              textSize="xmd"
              data={users}
              emptyValue={userEmptyValue}
              getPath={getPath}
              schema={userSchema}
              showHeader={false}
              stickyHeader={false}
            />
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] w-full min-h-[360px]">
              {users.length === 0 ? (
                <EmptyUserGridTile />
              ) : (
                users.map((user) => (
                  <UserGridTile
                    key={user.id}
                    user={user}
                    href={getPath(user)}
                  />
                ))
              )}
              {Array.from({ length: 8 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`filler-${i}`} className={TILE_BORDER} />
              ))}
            </div>
          )}
        </div>
      </PaymentViewLayout>
      {showCreateModal && (
        <CreateUserModal
          onClose={handleCloseModal}
          onCreated={handleCloseModal}
        />
      )}
    </>
  );
};

export const UsersListPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<UsersListPageLoading workspaceId={workspaceId} />}>
    <UsersListPageContent workspaceId={workspaceId} />
  </Suspense>
);
